<template>
    <div>
        <EditKrpano
            v-if="canBeEditable && data"
            :krpanoHotspot="data.krpanoHotspot"
            :sceneKey="data.sceneKey"
            :type="data.type"
            :widgetTitle="data.widgetTitle"
            :editProperties="editProperties"
            :defaultProperties="defaultProperties"
            :loaded="widgetLoaded"
            :updateJsonUrl="updateJsonUrl"
            @set-camera="setCamera"
            type="krpano"
        />
        <MyHabiteoStudio
            v-if="isStudioEnabled"
            :formUrl="formUrl"
            :zoomFactor="zoomFactor"
            :exportedBlobFile="exportedBlobFile"
            @export-media="exportMedia"
            @set-format="setImageFormat"
            @set-mention="setMention"
            @set-quality="setQuality"
            @set-preset="setPreset"
            @set-advanced_preset="setAdvancedPreset"
        />
        <div
            id="drone-view"
            ref="droneHolder"
            class="content-widget drone-view"
            :class="{ 
                'edit-mode': canBeEditable,
                'alternative-layout': data.type === 'plot',
                'has-funnel': hasFunnel,
                'standalone': standalone
            }"
        />

        <FunnelFooter v-if="hasFunnel"/>

        <!-- Exposure snackbar -->
        <v-snackbar
            v-model="snackbar['exposure']"
            class="snackbar mt-8"
            top
            :width="snackbarWidth"
            :style="{
                'left': this.snackbarOffsetLeft + 'px',
                'position':  isMediumAndUpScreen ? 'absolute' : 'fixed',
                'display': isMediumAndUpScreen ? 'block' : 'flex'
            }"
            :timeout="snackbarTimeout"
        >
            <v-layout class="snackbar-content text-center black--text">
                <span v-html="$t('tooltips.exposure', { 'exposure': plotExposure })"/>
            </v-layout>
        </v-snackbar>
        <!-- /Exposure snackbar -->
    </div>
</template>

<script>
    import _ from 'lodash';
    import { EventBus } from '@/event-bus';
    import Http from '@/api/data';
    import { mapActions, mapGetters } from 'vuex';

    import EditKrpano from "@/components/editMode/EditKrpano";
    import MyHabiteoStudio from "@/components/myHabiteoStudio/MyHabiteoStudio";
    import FunnelFooter from '@/components/funnel/FunnelFooter.vue';

    export default {
        name: 'Drone',
        components: {
            EditKrpano,
            MyHabiteoStudio,
            FunnelFooter
        },
        props: [
            'data',
            'link',
            'active',
            'standalone'
        ],
        data: () => ({
            krpanoWidget: null,
            editProperties: {
                'viewAngle': null,
                'zoomFactor': null,
                'north': null
            },
            defaultProperties: {
                'viewAngle': 0,
                'zoomFactor': 0,
                'north': null
            },
            mouseTimeout: null,
            widgetLoaded: false,
            hotspotList: [],
            formUrl: null,
            exportedBlobFile: null,
            snackbarTimeout: 7000,
            snackbar: {
                'exposure': false
            },
            snackbarWidth: 230,
            snackbarOffsetLeft: 0,
            currentScene: null
        }),
        computed: {
            ...mapGetters([
                'isInternal',
                'isOfflineMode',
                'isReviewUse',
                'language',
                'protocol',
                'startedWidget',
                'isIframe'
            ]),
            ...mapGetters('widget', [
                'requestedScene',
                'funnel'
            ]),
            isEditable() {
                return this.isInternal ? this.$route.query.edit !== undefined : false;
            },
            canBeEditable() {
                if (this.standalone && this.isEditable)
                    return true;
                return false;
            },
            widgetType() {
                if (this.$props.link) {
                    let pathname = new URL(this.$props.link).pathname;
                    return pathname.split('/')[2];
                } else
                    return this.startedWidget;
            },
            isExportMediaEnabled() {
                return this.standalone && this.$route.query.mediaExport !== undefined;
            },
            displayExposureTooltip() {
                return this.$props.data.type === 'plot' && this.plotExposure;
            },
            plotExposure() {
                let exposure = this.getPlotDetail('exposition');
                return exposure && exposure.value.split('/').length <= 2 ? exposure.value : null;
            },
            isMediumAndUpScreen() {
                return this.$vuetify.breakpoint.mdAndUp || this.$vuetify.breakpoint.smAndUp && this.$vuetify.breakpoint.height > 1000;
            },
            updateJsonUrl() {
                return this.$props.data.private ? this.$props.data.private.krpanoURI : null;
            },
            isStudioEnabled() {
                return this.isExportMediaEnabled && this.formUrl ? true : false;
            },
            zoomFactor() {
                return this.editProperties && this.editProperties.zoomFactor  ? this.editProperties.zoomFactor : 0;
            },
            apiUrl() {
                return this.protocol + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            },
            hasFunnel() {
                return this.funnel && !this.isFunnel;
            },
            isFunnel() {
                return this.isIframe && document.referrer === process.env.FUNNEL_URL;
            }
        },
        async mounted() {
            this.render();

            EventBus.$on('setNorth', () => {
                this.krpanoWidget.widget.north = this.editProperties.north;
            });
        },
        beforeDestroy() {
            if (this.krpanoWidget)
                this.krpanoWidget.destroy();
        },
        methods: {
            ...mapActions('widget', [
                'setWidgetScene'
            ]),
            ...mapActions([
                'setIsLoading'
            ]),
            render() {
                let urlLink = this.$props.link ? this.$props.link : this.apiUrl + '/request-data/' + this.widgetType + window.location.search;
                if (this.language && !urlLink.includes('&language'))
                    urlLink += '&language=' + this.language;
                if (this.isOfflineMode && !urlLink.includes('&offline'))
                    urlLink += '&offline';
                Http.getData(urlLink).then(payload => {
                    if (payload) {
                        if (payload.language)
                            this.$i18n.locale = payload.language;

                        let vm = this;
                        payload = Object.assign(payload, {
                            callbacks: {
                                sceneLoadedCallback(scene, newRoom) {
                                    // Reset default properties.
                                    vm.defaultProperties.viewAngle = [scene.camera[0], scene.camera[1]];
                                    vm.defaultProperties.north = scene.camera[0];
                                    vm.defaultProperties.zoomFactor = scene.camera[2];
                                    vm.hotspotList = _.cloneDeep(scene.hotspots);

                                    var data = Object.assign(vm.data, {
                                        'krpanoHotspot': scene.hotspots,
                                        'sceneKey': scene.id
                                    });
                                    vm.setWidgetScene(scene.key);
                                    EventBus.$emit('updateSidebar', data, false);

                                    vm.widgetLoaded = true;
                                },
                                viewChangeCallback(camera, index) {
                                    // Setup mouse handler.
                                    vm.mouseTimeout && clearTimeout(vm.mouseTimeout);
                                    vm.mouseTimeout = setTimeout(() => {
                                        vm.editProperties.viewAngle = [camera[0], camera[1]];
                                        vm.editProperties.north = camera[0];
                                        vm.editProperties.zoomFactor = camera[2];
                                    }, 1000);
                                },
                                widgetReadyCallback(widget, data) {
                                    // Hide disabled hotspots on production mode
                                    let scenes = data.scenes;
                                    let activeScene = scenes.findIndex(scene => scene.active);
                                    let hotspots = scenes[activeScene].hotspots;
    
                                    if (vm.$props.data.gui)
                                        data.gui = vm.$props.data.gui;
                                    if (hotspots)
                                        hotspots.forEach(hotspot => {
                                            if (((hotspot.ath == null || hotspot.ath == 0) && (hotspot.atv == null || hotspot.atv == 0)) || (hotspot.hasOwnProperty('active') && !hotspot.active))
                                                hotspot.visible = false;
                                        });

                                    vm.setIsLoading(false);
                                },
                                exportMediaCallback(blob) {
                                    vm.exportedBlobFile = blob;
                                },
                                goToDestinationCallback(hotspot) {
                                    vm.goToWidget(hotspot);
                                }
                            }
                        });

                        payload.type = this.widgetType !== 'drone-floor-view' ? 'drone-view' : this.widgetType;
                        payload.widgetType = this.widgetType !== 'drone-floor-view' ? 'drone-view' : this.widgetType;
                        if (this.requestedScene) {
                            this.currentScene = this.requestedScene;
                            payload.requestedScene = this.requestedScene;
                        }

                        if (this.canBeEditable)
                            payload.showCompass = this.canBeEditable ? true : false;

                        if (this.isExportMediaEnabled && (payload.private && 'studioExport' in payload.private))
                            this.formUrl = payload.private.studioExport;

                        payload.isStudioEnabled = this.isStudioEnabled;

                        let jsonAPIUrl = this.isReviewUse ? payload.private.panoURI : payload.private.krpanoURI;
                        this.getKrpanoConfig(jsonAPIUrl, jsonData => {
                            payload.jsonConfig = jsonData;
                            if (this.isReviewUse) {
                                jsonData['scenes'].forEach(scene => {
                                    if (scene.hotspots)
                                        scene.hotspots.sort((a, b) => a.name && b.name ? (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1) : a.name ? 1 : -1);
                                });
                                this.krpanoWidget = new Habiteo.PanoWidget(this.$refs.droneHolder, jsonData, document.getElementById('fullScreen'));
                            } else {
                                Object.keys(jsonData).forEach(scene => {
                                    if (jsonData[scene].hotspots)
                                        jsonData[scene].hotspots.sort((a, b) => a.name && b.name ? (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1) : a.name ? 1 : -1);
                                });
                                this.krpanoWidget = new Habiteo.VirtualTourWidget(this.$refs.droneHolder, payload, document.getElementById('fullScreen'));
                            }
                        });

                        if (this.standalone)
                            EventBus.$emit('setSidebarData', payload);

                        EventBus.$emit('trackWidget', payload.tracking);
                        this.snackbar['exposure'] = this.displayExposureTooltip;
                        if (this.isMediumAndUpScreen)
                            this.setSnackbarOffset();
                    }
                }).catch(error => {
                    this.errorState = true;
                    console.error('Error :', error);
                });
            },
            setSnackbarOffset() {
                this.snackbarOffsetLeft = this.$refs.droneHolder ? (this.$refs.droneHolder.offsetWidth / 2) - (this.snackbarWidth / 2) - 50 : 0;
                this.snackbarOffsetLeft += document.getElementById('sidebar') ? document.getElementById('sidebar').offsetWidth : 0;
            },
            getKrpanoConfig(apiUrl, callback) {
                if (apiUrl)
                    Http.getData(apiUrl).then(payload => {
                        callback(payload);
                    }).catch(error => {
                        // this.errorState = true;
                        console.error('API request error :', error);
                        callback(null);
                    });
                else
                    console.error('API URL is not valid');
            },
            exportMedia(settings) {
                this.krpanoWidget.widget.shotPano(settings);
            },
            setImageFormat(format) {
                this.krpanoWidget.widget.exportFormat = format;
            },
            setPreset(preset) {
                this.krpanoWidget.widget.preset = preset;
            },
            setAdvancedPreset(advancedPreset){
                this.krpanoWidget.widget.preset = advancedPreset;
            },
            setMention(option) {
                this.krpanoWidget.widget.mention = option;
            },
            setQuality(quality) {
                this.krpanoWidget.widget.exportQuality = quality;
            },
            getPlotDetail(key) {
                let detail = this.data.details ? this.data.details.filter(detail => detail.id === key) : [];
                return detail.length ? detail[0] : null;
            },
            goToWidget(hotspot) {
                if (hotspot) {
                    if (!this.standalone) {
                        let widgetList = this.$props.data.widgetList || this.$props.data.levels;
                        let activeWidget = widgetList.filter(el => el.active);
                        if (hotspot.linkType && hotspot.linkType === 'digital-model') {
                            // // TODO : fix digital-model navigation on program widget
                            // let sidebarItems = widgetList.filter(w => hotspot.linkType === w.widgetType);
                            // if (sidebarItems) {
                            //     sidebarItems.forEach(widget => {
                            //         let hotspotUrlObj = new URL(hotspot.link);
                            //         let hotspotUrlParams = new URLSearchParams(hotspotUrlObj.search);
                            //         let hotspotUrl = hotspotUrlObj.origin + hotspotUrlObj.pathname + `?id=${hotspotUrlParams.get('id')}&key=${hotspotUrlParams.get('key')}`;

                            //         let widgetUrlObj = widget.link ? new URL(widget.link) : null;
                            //         let widgetUrlParams = widgetUrlObj ? new URLSearchParams(widgetUrlObj.search) : null;
                            //         let widgetUrl = widgetUrlParams ? `${widgetUrlObj.origin}${widgetUrlObj.pathname}?id=${widgetUrlParams.get('id')}&key=${widgetUrlParams.get('key')}` : null;

                            //         if (hotspotUrl === widgetUrl) {
                            //             let payload = {};

                            //             if ('levels' in this.$props.data)
                            //                 payload.levels = widgetList;
                            //             else
                            //                 payload.items = widgetList;

                            //             EventBus.$emit('updateSidebar', payload, false);
                            //             EventBus.$emit('hotspot_setActive', widget, hotspot);
                            //             return;
                            //         }
                            //     });
                            // }
                            EventBus.$emit('hotspot_setActive', activeWidget[0], hotspot);
                        } else {
                            EventBus.$emit('hotspot_setActive', activeWidget[0], hotspot);
                        }
                    } else {
                        switch (this.startedWidget) {
                            default:
                                EventBus.$emit('saveSidebar');
                                EventBus.$emit('updateLoadedWidgets', {
                                    id: hotspot.linkType,
                                    link: hotspot.link,
                                    active: true,
                                    widgetType: hotspot.linkType
                                });
                            break;
                        }
                    }
                }
            },
            setCamera(data) {
                if (data.metadata) {
                    let camera;
                    let currentScene = this.krpanoWidget.data.scenes.find(scene => scene.id === data.key);
                    if (data.metadata['viewAngle'] && data.metadata['zoomFactor']) {
                        camera = data.metadata['viewAngle'].concat(data.metadata['zoomFactor']);
                    }
                    else if (data.metadata['viewAngle']) {
                        camera = data.metadata['viewAngle'].concat([currentScene.camera[2]]);
                    }
                    else {
                        camera = [currentScene.camera[0], currentScene.camera[1]].concat(data.metadata['zoomFactor']);
                    }
                    this.krpanoWidget.widget.setCamera(data.key, camera);
                }
            }
        },
        watch: {
            active() {
                if (this.active)
                    this.render();
            },
            requestedScene(newScene, lastScene) {
                if (newScene !== lastScene && this.currentScene !== newScene) {
                    let firstSceneKey = this.krpanoWidget.data.scenes[0].key;
                    if (newScene) {
                        this.currentScene = newScene;
                        this.krpanoWidget.widget.loadRequestedScene(newScene);
                    }
                    else if (newScene === null)
                        if (firstSceneKey !== this.currentScene) {
                            this.currentScene = firstSceneKey;
                            this.krpanoWidget.widget.loadRequestedScene(firstSceneKey);
                        }
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .drone-view {
        position: relative;
        .content-widget {
            &.has-funnel {
                height: calc(100% - 4%);
            }
        }
    }
</style>


// WEBPACK FOOTER //
// src/views/DroneWidget.vue